import React, { useState, useEffect } from 'react';
import { User, Upload, Save, MapPin, Briefcase, Euro } from 'lucide-react';
import AdminDoctorService from '../../services/admin.doctor.service';
import { UPLOAD_PREFIX } from '../../config/appConfig';
import PropTypes from 'prop-types'; 
import AdminChangePasswordBoxDoctor from './AdminChangePasswordBoxDoctor';


const DoctorDetailSettings = ({ doctorId, onUpdate }) => {
  const [profileData, setProfileData] = useState({
    biography: "",
    professional_story: "",
    default_price_hour: "",
    profile_picture: null,
    specialization: "",
    office_address: "",
    phone: "",
    tags: [],
    commission_rate: ""
  });

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [provenanceCode, setProvenanceCode] = useState('');


  useEffect(() => {
    loadProfile();
  }, [doctorId]);

  const loadProfile = async () => {
    try {
      const data = await AdminDoctorService.getProfile(doctorId);
      setProfileData({
        biography: data.biography || '',
        professional_story: data.professional_story || '',
        default_price_hour: data.default_price_hour || '',
        profile_picture: data.profile_picture,
        specialization: data.specialization || '',
        office_address: data.office_address || '',
        phone: data.user?.phone || '',
        tags: data.tags || [],
        commission_rate: data.commission_rate || '',
      });
    } catch (error) {
      console.error('[DEBUG] Error loading profile:', error);
    }
  };

  // Quando l'utente modifica un campo, puliamo i messaggi di errore e successo
  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccessMessage('');
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Verifica la dimensione del file (2MB = 2 * 1024 * 1024 bytes)
    const maxSize = 2 * 1024 * 1024; // 2MB
    
    if (file.size > maxSize) {
      // Notifica all'utente che il file è troppo grande
      alert("L'immagine è troppo grande. La dimensione massima consentita è 2MB.");
      // Reset del campo di input file
      event.target.value = '';
      return;
    }
    
    try {
      const response = await AdminDoctorService.uploadProfilePicture(doctorId, file);
      setProfileData(prev => ({
        ...prev,
        profile_picture: response.profile_picture
      }));
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('[DEBUG] Error uploading profile picture:', error);
      
      // Mostra un messaggio di errore specifico per dimensione del file
      if (error.message && error.message.includes('413')) {
        alert("L'immagine è troppo grande. La dimensione massima consentita è 2MB.");
      } else {
        alert("Si è verificato un errore durante il caricamento dell'immagine.");
      }
    }
  };

  const handleSave = async () => {

    if (!profileData.phone || profileData.phone.trim() === '') {
      setError('Il numero di telefono è obbligatorio');
      return;
    }

    setIsSaving(true);
    try {
      await AdminDoctorService.updateProfile(doctorId, {
        biography: profileData.biography,
        professional_story: profileData.professional_story,
        default_price_hour: profileData.default_price_hour,
        specialization: profileData.specialization,
        office_address: profileData.office_address,
        phone: profileData.phone.trim(),
        commission_rate: profileData.commission_rate,
      });
      if (onUpdate) onUpdate();
      setError(''); // Puliamo eventuali errori precedenti
    } catch (error) {
      setError(error.message || 'Errore durante l\'aggiornamento del profilo');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-600 rounded-md">
          {successMessage}
        </div>
      )}

      {/* Foto Profilo */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold mb-4">Foto Profilo</h2>
        <div className="flex items-center space-x-6">
          {profileData.profile_picture ? (
            <img
              src={`${UPLOAD_PREFIX}/storage/${profileData.profile_picture}`}
              alt="Profile"
              className="w-24 h-24 rounded-full object-cover"
            />
          ) : (
            <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center">
              <User className="w-12 h-12 text-purple-600" />
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleProfilePictureUpload}
            className="hidden"
            id="profile-picture-input"
          />
          <label
            htmlFor="profile-picture-input"
            className="px-4 py-2 border border-purple-600 text-purple-600 rounded-md hover:bg-purple-50 flex items-center cursor-pointer"
          >
            <Upload className="w-4 h-4 mr-2" />
            Carica nuova foto
          </label>
        </div>
      </div>

      <AdminChangePasswordBoxDoctor doctorId={doctorId} />


      {/* Informazioni Base */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <Briefcase className="w-5 h-5 mr-2 text-purple-600" />
          Informazioni Professionali
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Telefono <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={profileData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={`w-full p-2 border ${error && !profileData.phone
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-purple-500'
                } rounded-md focus:ring-2 focus:border-transparent`}
              required
            />
            {error && !profileData.phone && (
              <p className="mt-1 text-sm text-red-500">{error}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Indirizzo Studio</label>
            <div className="relative">
              <MapPin className="w-5 h-5 absolute left-2 top-2.5 text-gray-400" />
              <input
                type="text"
                value={profileData.office_address}
                onChange={(e) => handleInputChange('office_address', e.target.value)}
                className="w-full p-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bio e Specializzazioni */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold mb-4">Profilo Professionale</h2>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Biografia</label>
            <textarea
              value={profileData.biography}
              onChange={(e) => handleInputChange('biography', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows={4}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Storia Professionale</label>
            <textarea
              value={profileData.professional_story}
              onChange={(e) => handleInputChange('professional_story', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Specializzazione</label>
            <input
              type="text"
              value={profileData.specialization}
              onChange={(e) => handleInputChange('specialization', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>


          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center">
                <Euro className="w-5 h-5 mr-2 text-gray-400" />
                Commissione (%)
              </div>
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={profileData.commission_rate}
              onChange={(e) => handleInputChange('commission_rate', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

       {/* Pulsante Salva */}
       <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center ${isSaving ? 'opacity-50 cursor-not-allowed' : ''
            }`}
        >
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Salvataggio...' : 'Salva Modifiche'}
        </button>
      </div>
     </div>
  );
};

DoctorDetailSettings.propTypes = {
  doctorId: PropTypes.string.isRequired, 
  onUpdate: PropTypes.func.isRequired   
};

export default DoctorDetailSettings;